import {
  AuthContext,
  JWT,
  Text,
  getParsedAccessToken,
} from "@hackthenorth/north";
import Cookies from "js-cookie";
import { Button, InlineError, TextInput } from "north.js";
import React, { useEffect, useState } from "react";

import Header from "src/shared/components/Header";
import PageWrapper from "src/shared/components/PageWrapper";
import { API_HOST_URL, IS_LOCAL_API } from "src/shared/utils/env";

import "twin.macro";

// North's default auth redirects and refreshes against hosted auth. Keep the
// explicitly selected localhost workflow entirely on the local API instead.
export const LocalAuthProvider: React.FC<{ production: boolean }> = ({
  children,
}) => {
  const [token, setToken] = useState<JWT | null>(
    getParsedAccessToken() || null
  );
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!token) return;
    const timer = window.setTimeout(
      () => setExpired(true),
      Math.max(0, token.exp * 1000 - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [token]);
  return (
    <AuthContext.Provider
      value={{
        token,
        tokenExpired: expired,
        loginUrl: (path) =>
          `/local-login?next=${encodeURIComponent(
            path || window.location.pathname
          )}`,
        signupUrl: () => "/local-login",
        logOut: () => {
          Cookies.remove("accessJwt", { path: "/" });
          setToken(null);
          setExpired(false);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function LocalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  if (!IS_LOCAL_API) return null;
  return (
    <>
      <Header
        title="Local development login"
        subtitle="Sign in to your local dashboard"
      />
      <PageWrapper>
        <form
          tw="font-body"
          aria-label="Local development login"
          aria-describedby="local-login-help"
          aria-busy={busy}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 24,
            maxWidth: 440,
          }}
          onSubmit={async (event) => {
            event.preventDefault();
            if (busy) return;
            setBusy(true);
            setMessage("");
            try {
              const response = await fetch(`${API_HOST_URL}/v3/graphql`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  query:
                    "mutation LocalLogin($email: String!, $password: String!) { loginWithPassword(email: $email, password: $password) { accessJwt } }",
                  variables: { email: email.trim(), password },
                }),
                credentials: "omit",
                redirect: "error",
              });
              const result = await response.json();
              const jwt = result.data?.loginWithPassword?.accessJwt;
              if (!response.ok || !jwt || result.errors?.length)
                throw new Error();
              Cookies.remove("refreshJwt", { path: "/" });
              Cookies.set("accessJwt", jwt, { sameSite: "strict", path: "/" });
              const next =
                new URL(window.location.href).searchParams.get("next") || "/";
              window.location.assign(
                next.startsWith("/") &&
                  !next.startsWith("//") &&
                  !next.includes("\\")
                  ? next
                  : "/"
              );
            } catch {
              setMessage(
                "Could not sign in. Check your email and password, and make sure your local HackerAPI is running and the account is set up for password login."
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <Text mods="grey" id="local-login-help">
            Use the email and password for an account in your local HackerAPI
            database.
          </Text>
          <label
            htmlFor="local-login-email"
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <Text mods="bold textBody">Email</Text>
            <TextInput
              tw="w-full font-body text-16"
              id="local-login-email"
              name="email"
              type="email"
              size="md"
              placeholder="Enter your email"
              autoComplete="username"
              required
              disabled={busy}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label
            htmlFor="local-login-password"
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <Text mods="bold textBody">Password</Text>
            <TextInput
              tw="w-full font-body text-16"
              id="local-login-password"
              name="password"
              type="password"
              size="md"
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              disabled={busy}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {message && <InlineError role="alert">{message}</InlineError>}
          <Button
            tw="font-body"
            type="submit"
            color="primary"
            size="md"
            disabled={busy}
            style={{ alignSelf: "flex-start" }}
          >
            {busy ? "Signing in…" : "Sign in locally"}
          </Button>
        </form>
      </PageWrapper>
    </>
  );
}
